import type { paths as Ops } from '@cortex/api-client';

// Response shapes of the versions, search, alerts and audit APIs, from the generated client.

type Json<P extends keyof Ops, M extends keyof Ops[P]> = Ops[P][M] extends {
  responses: { 200: { content: { 'application/json': infer T } } };
}
  ? T
  : never;

export type AlertEvent = Json<'/api/alerts/feed', 'get'>['data'][number];
export type AlertRule = Json<'/api/alerts/projects/{slug}/rules', 'get'>['rules'][number];
export type DesignCommit = Json<
  '/api/versions/projects/{slug}/branches/{branch}/commits',
  'get'
>['commits'][number];
export type TableStat = Json<'/api/versions/projects/{slug}/branches/{branch}/diff', 'get'>['tables'][number];
export type TableDiff = Json<'/api/versions/projects/{slug}/branches/{branch}/diff/{table}', 'get'>;
export type RowChange = TableDiff['rows'][number];
export type RestoreResult = Json<'/api/versions/projects/{slug}/branches/{branch}/restore', 'post'>;
export type SearchGroup = Json<'/api/search', 'get'>['groups'][number];
export type AuditEvent = Json<'/api/audit/events', 'get'>['data'][number];

/** When something happened, as the old pages said it ("5m ago", then the date). */
export function timeAgo(ts: string | null | undefined, now = Date.now()): string {
  if (!ts) return '';
  const d = new Date(ts);
  const min = Math.floor((now - d.getTime()) / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return d.toLocaleDateString();
}

export const shortHash = (h: string | null | undefined) => (h ? h.slice(0, 8) : '—');

export const fmtTime = (v: string | null | undefined) =>
  v
    ? new Date(v).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

/** A cell value of a design row, readable in one line. */
export function cellText(v: unknown): string {
  if (v === null || v === undefined) return '∅';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** Event fired when alerts change, so the sidebar badge refreshes at once. */
export const ALERTS_CHANGED_EVENT = 'cortex:alerts-changed';
